if (!customElements.get('speakon-home-intelligence')) {
  customElements.define('speakon-home-intelligence', class extends HTMLElement {
    connectedCallback() {
      this.stop?.();
      const controller = new AbortController();
      const options = { signal: controller.signal };
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const demos = [...this.querySelectorAll('[data-demo]')].map(el => ({ el, visible: false, elapsed: 0, index: 0 }));
      const dictionary = demos[0];
      const rows = [...dictionary.el.querySelectorAll('.sp-intelligence__term')];
      const add = dictionary.el.querySelector('.sp-intelligence__add');
      const label = add.querySelector('[data-dictionary-label]');
      const confetti = dictionary.el.querySelector('.sp-intelligence__confetti');
      let frame = 0, last = 0;

      // Motion and timings follow the three supplied SpeakON demo sites.
      const pieces = [[-174,-184,-190,0],[-142,-232,145,40],[-112,-152,-110,90],[-82,-264,210,10],[-50,-198,-155,120],[-22,-286,185,65],[20,-242,-145,20],[54,-176,215,110],[86,-274,-200,50],[118,-208,170,130],[152,-246,-135,75],[174,-164,225,25]];
      confetti.replaceChildren(...pieces.map(([x,y,rotation,delay], i) => {
        const piece = document.createElement('span');
        piece.style.cssText = `--piece-x:${x}px;--piece-y:${y}px;--piece-rotation:${rotation}deg;--piece-delay:${delay}ms;--piece-color:${['#16864b','#e6bd4f','#de7669','#18181c'][i % 4]}`;
        return piece;
      }));
      const renderDictionary = (time, complete = false) => {
        const t = time % (700 + 330 + rows.length * 160 + 2380);
        const phase = complete ? 'done' : t < 700 ? 'idle' : t < 700 + 330 + rows.length * 160 + 180 ? 'adding' : 'done';
        rows.forEach((row, i) => row.classList.toggle('is-added', complete || t >= 1030 + i * 160));
        if (dictionary.el.dataset.phase !== phase) {
          dictionary.el.dataset.phase = phase;
          label.textContent = add.dataset[phase === 'idle' ? 'idleLabel' : phase === 'adding' ? 'addingLabel' : 'doneLabel'].replace('{count}', rows.length);
          add.classList.remove('is-changing');
          void add.offsetWidth;
          add.classList.add('is-changing');
        }
      };

      const placeSelector = (demo, animate = true) => {
        if (demo.el.dataset.demo !== 'tone' || !demo.buttons.length) return;
        const selector = demo.el.querySelector('.sp-intelligence__selector');
        const tab = demo.buttons[demo.index];
        if (!animate) selector.style.transition = 'none';
        selector.style.width = `${tab.offsetWidth}px`;
        selector.style.height = `${tab.offsetHeight}px`;
        selector.style.transform = `translate(${tab.offsetLeft}px,${tab.offsetTop}px)`;
        if (!animate) { void selector.offsetWidth; selector.style.removeProperty('transition'); }
      };
      const select = (demo, index) => {
        const tone = demo.el.dataset.demo === 'tone';
        const previous = demo.index;
        const animate = !reduced.matches;
        demo.index = index;
        demo.elapsed = 0;
        demo.buttons.forEach((button, i) => {
          button.setAttribute(tone ? 'aria-pressed' : 'aria-selected', String(i === index));
          if (!tone) button.tabIndex = i === index ? 0 : -1;
        });
        demo.layers.forEach((layer, i) => {
          layer.classList.remove('is-current', 'is-entering', 'is-leaving');
          layer.setAttribute('aria-hidden', String(i !== index));
          layer.inert = i !== index;
        });
        if (tone && previous !== index && animate) demo.layers[previous].classList.add('is-leaving');
        const next = demo.layers[index];
        void next.offsetWidth;
        next.classList.add('is-current');
        if (animate) next.classList.add('is-entering');
        placeSelector(demo, animate);
      };
      demos.slice(1).forEach(demo => {
        const tone = demo.el.dataset.demo === 'tone';
        demo.buttons = [...demo.el.querySelectorAll(tone ? '.sp-intelligence__tone' : '[role="tab"]')];
        demo.layers = [...demo.el.querySelectorAll(tone ? '.sp-intelligence__tone-layer' : '.sp-intelligence__voice-layer')];
        demo.interval = tone ? 1917 : 2000;
        demo.buttons.forEach((button, index) => {
          button.addEventListener('click', () => { select(demo, index); sync(); }, options);
          if (!tone) button.addEventListener('keydown', event => {
            let next;
            if (['ArrowRight','ArrowDown'].includes(event.key)) next = (index + 1) % demo.buttons.length;
            if (['ArrowLeft','ArrowUp'].includes(event.key)) next = (index + demo.buttons.length - 1) % demo.buttons.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = demo.buttons.length - 1;
            if (next === undefined) return;
            event.preventDefault();
            demo.buttons[next].focus();
            select(demo, next);
            sync();
          }, options);
        });
      });
      const canAdvance = demo => demo.visible && (demo === dictionary || demo.buttons.length > 1);
      const tick = now => {
        const delta = now - last;
        last = now;
        demos.forEach(demo => {
          if (!canAdvance(demo)) return;
          demo.elapsed += delta;
          if (demo === dictionary) renderDictionary(demo.elapsed);
          else if (demo.elapsed >= demo.interval) select(demo, (demo.index + 1) % demo.buttons.length);
        });
        frame = requestAnimationFrame(tick);
      };
      const sync = () => {
        cancelAnimationFrame(frame);
        const enabled = !document.hidden && !reduced.matches;
        demos.forEach(demo => { demo.el.dataset.running = String(enabled && demo.visible); });
        if (reduced.matches) renderDictionary(0, true);
        if (enabled && demos.some(canAdvance)) { last = performance.now(); frame = requestAnimationFrame(tick); }
      };
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => { demos.find(demo => demo.el === entry.target).visible = entry.isIntersecting; });
        sync();
      });
      demos.forEach(demo => observer.observe(demo.el));
      const resize = new ResizeObserver(() => placeSelector(demos[1], false));
      resize.observe(demos[1].el);
      document.addEventListener('visibilitychange', sync, options);
      reduced.addEventListener('change', sync, options);
      renderDictionary(0, reduced.matches);
      placeSelector(demos[1], false);
      document.fonts?.ready.then(() => {
        if (!controller.signal.aborted) placeSelector(demos[1], false);
      });
      this.classList.add('is-ready');
      sync();
      this.stop = () => {
        cancelAnimationFrame(frame);
        observer.disconnect();
        resize.disconnect();
        controller.abort();
        demos.forEach(demo => { demo.el.dataset.running = 'false'; });
        demos.slice(1).forEach(demo => { if (demo.buttons.length) select(demo, 0); });
      };
    }
    disconnectedCallback() { this.stop?.(); }
  });
}
